import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import Loan from "../../models/Loan";
import Leave from "../../models/Leave";
import { capitalizeFirst } from "../../utils/string";
import { formatDate } from "../../utils/date";

interface RequestDetailsModalProps {
    show: boolean;
    onClose: () => void;
    type: RequestType;
    data: Loan | Leave;
}


interface ApprovalStep {
    title: string;
    status: "approved" | "pending" | "rejected";
    approver?: string;
    date?: string;
}
const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
    show,
    onClose,
    type,
    data,
}) => {
    if (!show) return null;

    const { staffName, approvals } = data;

    const approvers = approvals?.map(app => ({
        title:
            capitalizeFirst(app?.approveBy?.portfolio),
        status: app?.status,
        approver: (
            app?.approveBy?.firstName +
            " " + app?.approveBy?.lastName 
        ),
        date: formatDate(app?.updatedAt)
    }));

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">

            <div className="bg-white rounded-lg 
                w-full sm:w-[28rem] md:w-[30rem] lg:w-[35rem] 
                max-w-[90vw] p-6 shadow-lg animate-fadeIn 
                max-h-[80vh] overflow-y-auto">

                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                        {type === "loan" ? "Loan Request Details" : "Leave Request Details"}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
                        {FaTimes({})}
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <InfoCard
                        title="Staff ID"
                        value={data.staffId ?? ""}
                    />

                    <InfoCard
                        title="Staff Name"
                        value={staffName}
                    />

                    {type === "leave" && (
                        <>
                            <InfoCard
                                title="Start Date"
                                value={(data as Leave).start}
                            />

                            <InfoCard
                                title="End Date"
                                value={(data as Leave).end}
                            />
                        </>
                    )}

                    {type === "loan" && (
                        <>
                            <InfoCard
                                title="Loan Amount"
                                value={`₦${(data as Loan).amount?.toLocaleString()}`}
                            />

                            <InfoCard
                                title="Duration"
                                value={`${(data as Loan).month} Months`}
                            />
                        </>
                    )}

                    <InfoCard
                        title="Application Date"
                        value={data.createdAt}
                    />

                    <InfoCard
                        title="Decision Date"
                        value={data.decisionDate || "-"}
                    />
                </div>

                {/* Notes Section */}
                {data.reason && (
                    <div className="mt-4 bg-gray-50 border rounded-xl p-4">
                        <h4 className="font-semibold mb-2">
                            Reason
                        </h4>

                        <p className="text-gray-600">
                            {data.reason}
                        </p>
                    </div>
                )}

                <ApprovalTimeline steps={approvers as ApprovalStep[]} />

            </div>
        </div>
    );
};

interface DetailItemProps {
    label: string;
    value: string | JSX.Element;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
    <div className="flex justify-between border-b pb-1">
        <span className="font-medium text-gray-700">{label}:</span>
        <span className="text-gray-900">{value}</span>
    </div>
);


interface InfoCardProps {
    title: string;
    value: string;
}

const InfoCard = ({
    title,
    value,
}: InfoCardProps) => (
    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            {title}
        </div>

        <div className="font-semibold text-gray-800">
            {value || "-"}
        </div>
    </div>
);



const ApprovalTimeline = ({ steps }: { steps: ApprovalStep[] }) => {
    return (
        <div className="mt-6">
            <h4 className="font-semibold text-gray-800 mb-4">
                Approval Progress
            </h4>

            <div className="space-y-4">
                {steps.map((step, index) => (
                    <div key={index} className="flex gap-4 relative">

                        {index !== steps.length - 1 && (
                            <div className="absolute left-4 top-8 h-full w-0.5 bg-gray-200" />
                        )}

                        <div
                            className={`
                                w-8 h-8 rounded-full flex items-center justify-center shrink-0
                                ${step.status === "approved"
                                    ? "bg-green-500 text-white"
                                    : step.status === "pending"
                                        ? "bg-orange-500 text-white animate-pulse"
                                        : step.status === "rejected"
                                            ? "bg-red-500 text-white"
                                            : "bg-gray-200 text-gray-500"
                                }
                            `}
                        >
                         {step.status === "approved"
  ? "✓"
  : step.status === "rejected"
    ? "✕"
    : "⏳"}
                        </div>

                        <div>
                            <div className="font-medium">{step.title}</div>

                            {step.approver && (
                                <div className="text-sm text-gray-500">
                                    {step.approver}
                                </div>
                            )}

                            {step.date && (
                                <div className="text-xs text-gray-400">
                                    {step.date}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


export default RequestDetailsModal;
